// The logged-in player (legacy player.js): login / register / logout / country change against the
// untouched PHP routes, the stored credentials (localStorage `solitaire_storage:player-data:name`
// and `:hash`, the hash IS the credential), and the silent login on page load.
// Fixes: credentials are stored only after the server said ok (ACCQ-001); a silent login whose
// session is gone re-sends the stored credentials once (ACCQ-002).
import { get, post, type ApiResponse } from '../api/client';
import { clientHash } from '../api/hashes';
import { env } from '../env';
import { uiStore, type Player } from '../store/uiStore';
import { withIndicator } from '../ui/busy';

const NAME_KEY = 'solitaire_storage:player-data:name';
const HASH_KEY = 'solitaire_storage:player-data:hash';
const LEGACY_KEYS = ['solitaire_storage:player-data:social', 'solitaire_storage:player-data:ac'];

/** Characters the name input drops (legacy Solitaire.Filter.UserName). */
export const NAME_FILTER = /[ `~!%()[\]{}<>?/"']/g; // legacy keyup filter (a backslash stays)
export const cleanName = (s: string): string => s.replace(NAME_FILTER, '').slice(0, 20);

const read = (k: string): string | null => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string | null) => {
  try {
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    /* no storage */
  }
};

export const storedCredentials = (): { name: string; hash: string } | null => {
  const name = read(NAME_KEY);
  const hash = read(HASH_KEY);
  return name && hash ? { name, hash } : null;
};

const storeCredentials = (name: string, hash: string) => {
  write(NAME_KEY, name);
  write(HASH_KEY, hash);
};
const clearCredentials = () => {
  for (const k of [NAME_KEY, HASH_KEY, ...LEGACY_KEYS]) write(k, null);
};

const toPlayer = (r: ApiResponse): Player | null =>
  r.result === 'ok' && r.response && typeof r.response === 'object' ? (r.response as Player) : null;

export type AuthResult = 'ok' | 'invalid' | 'error';

async function auth(action: 'login' | 'register', name: string, password: string): Promise<AuthResult> {
  const hash = clientHash(password);
  const r = await withIndicator(() => post(`auth/${action}.html`, { name, hash, userAction: 'true' }));
  const player = toPlayer(r);
  if (player) {
    storeCredentials(name, hash);
    uiStore().set({ player });
    return 'ok';
  }
  return r.result === 'invalid' ? 'invalid' : 'error';
}

export const login = (name: string, password: string) => auth('login', name, password);
export const register = (name: string, password: string) => auth('register', name, password);

/**
 * On load: the server session decides. The stored credentials travel as headers of that GET (never in
 * the URL), so the Node backend logs the player in again when the session cookie is gone. The PHP
 * backend ignores the headers and answers `invalid`; then the credentials are POSTed once.
 */
export async function silentLogin(): Promise<void> {
  if (env.appTarget === 'offline') return;
  const creds = storedCredentials();
  if (!creds) return;
  const details = await get('auth/user-details.html', {}, { 'X-Po-Name': creds.name, 'X-Po-Hash': creds.hash });
  const player = toPlayer(details);
  if (player) {
    uiStore().set({ player });
    return;
  }
  if (details.result !== 'invalid') return; // network / server error: keep the credentials, stay logged out
  const r = await post('auth/login.html', { name: creds.name, hash: creds.hash, userAction: 'true' });
  const again = toPlayer(r);
  if (again) uiStore().set({ player: again });
  else if (r.result === 'invalid') clearCredentials();
}

export async function logout(): Promise<void> {
  clearCredentials();
  uiStore().set({ player: null });
  await withIndicator(() => post('auth/logout.html', {}));
}

export async function changeCountry(code: string): Promise<boolean> {
  const r = await withIndicator(() => post('auth/change-country-code.html', { code }));
  const player = toPlayer(r);
  if (player) uiStore().set({ player });
  return !!player;
}
